import React, { useEffect, useState } from "react";
import {
  Box, Typography, Button, TextField, Chip, CircularProgress,
  InputAdornment, Divider, Avatar,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import StorefrontIcon from "@mui/icons-material/Storefront";
import { CiSearch } from "react-icons/ci";
import { useParams } from "react-router-dom";

const API_URL = process.env.REACT_APP_API_URL;
const LARAVEL_URL = process.env.REACT_APP_LARAVEL_API;

interface PortalProduct {
  id: number;
  productName: string;
  productShortDescription?: string;
  productImg?: string;
  productPrice?: string;
  wholesaleprice?: string;
  custom_price?: number | null;
}

interface ShopInfo {
  id: number;
  customerCompanyName?: string;
  customerFirstName?: string;
  customerLastName?: string;
  customerImg?: string;
}

const STATUS_COLORS: Record<string, string> = {
  pending: "#ff9800",
  confirmed: "#1976d2",
  processing: "#7b1fa2",
  delivered: "#2e7d32",
  cancelled: "#c62828",
};

const Portal: React.FC = () => {
  const { shopId } = useParams<{ shopId: string }>();

  const [shop, setShop] = useState<ShopInfo | null>(null);
  const [products, setProducts] = useState<PortalProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [cart, setCart] = useState<Record<number, number>>({});
  const [notes, setNotes] = useState("");
  const [search, setSearch] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [orderId, setOrderId] = useState<number | null>(null);
  const [cartOpen, setCartOpen] = useState(false);

  useEffect(() => {
    if (!shopId) return;
    fetch(`${API_URL}/restaurant-portal/${shopId}`)
      .then((r) => r.json())
      .then((d) => {
        if (!d.success) throw new Error("Not found");
        setShop(d.shop);
        setProducts(d.products || []);
      })
      .catch(() => setError("Restaurant not found or not available."))
      .finally(() => setLoading(false));
  }, [shopId]);

  const getPrice = (p: PortalProduct): number => {
    if (p.custom_price != null) return Number(p.custom_price);
    if (p.wholesaleprice) return Number(p.wholesaleprice);
    if (p.productPrice) return Number(p.productPrice);
    return 0;
  };

  const cartItems = products.filter((p) => (cart[p.id] || 0) > 0);
  const cartTotal = cartItems.reduce((s, p) => s + getPrice(p) * cart[p.id], 0);
  const cartCount = Object.values(cart).reduce((s, v) => s + v, 0);

  const adjust = (id: number, delta: number) => {
    setCart((prev) => {
      const next = (prev[id] || 0) + delta;
      return { ...prev, [id]: Math.max(0, next) };
    });
  };

  const handleSubmit = async () => {
    if (cartItems.length === 0) return;
    setSubmitting(true);
    try {
      const items = cartItems.map((p) => ({
        product_id: p.id,
        name: p.productName,
        qty: cart[p.id],
        price: getPrice(p),
        subtotal: getPrice(p) * cart[p.id],
      }));
      const res = await fetch(`${API_URL}/restaurant-portal/${shopId}/order`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items, total: cartTotal, notes }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to submit order");
      setOrderId(data.data?.id);
      setSubmitted(true);
    } catch (e: any) {
      alert(e.message || "Failed to submit order");
    } finally {
      setSubmitting(false);
    }
  };

  const resetOrder = () => {
    setCart({});
    setNotes("");
    setSubmitted(false);
    setOrderId(null);
    setCartOpen(false);
  };

  const filtered = products.filter((p) =>
    p.productName.toLowerCase().includes(search.toLowerCase())
  );

  const shopName =
    shop?.customerCompanyName ||
    [shop?.customerFirstName, shop?.customerLastName].filter(Boolean).join(" ") ||
    "Restaurant Portal";

  if (loading) {
    return (
      <Box
        sx={{
          position: "fixed", inset: 0, bgcolor: "#f5f6fa", zIndex: 9999,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          position: "fixed", inset: 0, bgcolor: "#f5f6fa", zIndex: 9999,
          display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 2,
        }}
      >
        <StorefrontIcon sx={{ fontSize: 60, color: "#c5cae9" }} />
        <Typography fontSize={16} color="text.secondary">{error}</Typography>
      </Box>
    );
  }

  /* ── ORDER CONFIRMED SCREEN ───────────────────────────────────────── */
  if (submitted) {
    return (
      <Box
        sx={{
          position: "fixed", inset: 0, bgcolor: "#f0fdf4", zIndex: 9999,
          display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column",
          gap: 3, px: 3, textAlign: "center",
        }}
      >
        <CheckCircleIcon sx={{ fontSize: 90, color: "#4caf50" }} />
        <Typography fontSize={26} fontWeight={800} color="#2e7d32">Order Placed!</Typography>
        <Typography fontSize={14} color="text.secondary" maxWidth={340}>
          Your order #{orderId} has been submitted successfully. The supplier will confirm it shortly.
        </Typography>
        <Box sx={{ bgcolor: "#fff", borderRadius: "12px", p: 2.5, minWidth: 280, boxShadow: "0 4px 20px rgba(0,0,0,0.08)" }}>
          {cartItems.map((p) => (
            <Box key={p.id} sx={{ display: "flex", justifyContent: "space-between", mb: 0.75 }}>
              <Typography fontSize={13} color="text.primary">{p.productName} × {cart[p.id]}</Typography>
              <Typography fontSize={13} fontWeight={600}>${(getPrice(p) * cart[p.id]).toFixed(2)}</Typography>
            </Box>
          ))}
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography fontSize={14} fontWeight={700}>Total</Typography>
            <Typography fontSize={14} fontWeight={700} color="#2e7d32">${cartTotal.toFixed(2)}</Typography>
          </Box>
          {notes && (
            <Typography fontSize={12} color="text.secondary" mt={1} fontStyle="italic">Note: {notes}</Typography>
          )}
        </Box>
        <Button
          variant="contained"
          onClick={resetOrder}
          sx={{ borderRadius: "10px", px: 4, py: 1.25, fontWeight: 700, bgcolor: "#2e7d32", "&:hover": { bgcolor: "#1b5e20" } }}
        >
          Place Another Order
        </Button>
      </Box>
    );
  }

  /* ── MAIN PORTAL ────────────────────────────────────────────────────── */
  return (
    <Box sx={{ position: "fixed", inset: 0, bgcolor: "#f5f6fa", zIndex: 9999, display: "flex", flexDirection: "column", overflow: "hidden" }}>

      {/* Header */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #1a237e 0%, #3949ab 60%, #5c6bc0 100%)",
          px: { xs: 2, md: 4 }, py: 2,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {shop?.customerImg ? (
            <Avatar
              src={`${LARAVEL_URL}/${shop.customerImg}`}
              sx={{ width: 40, height: 40, border: "2px solid rgba(255,255,255,0.4)" }}
            />
          ) : (
            <Box
              sx={{
                width: 40, height: 40, borderRadius: "10px",
                bgcolor: "rgba(255,255,255,0.2)", border: "2px solid rgba(255,255,255,0.35)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <Typography sx={{ fontWeight: 800, color: "#fff", fontSize: 18 }}>
                {shopName.charAt(0).toUpperCase()}
              </Typography>
            </Box>
          )}
          <Box>
            <Typography sx={{ fontWeight: 800, color: "#fff", fontSize: 16, lineHeight: 1.2 }}>
              {shopName}
            </Typography>
            <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.7)" }}>Order Portal</Typography>
          </Box>
        </Box>

        {/* Cart toggle */}
        <Button
          variant="contained"
          onClick={() => setCartOpen((v) => !v)}
          startIcon={<ShoppingCartIcon sx={{ fontSize: 18 }} />}
          sx={{
            bgcolor: cartCount > 0 ? "#ff9800" : "rgba(255,255,255,0.2)",
            fontWeight: 700, fontSize: 13, borderRadius: "10px", px: 2,
            "&:hover": { bgcolor: cartCount > 0 ? "#f57c00" : "rgba(255,255,255,0.3)" },
            boxShadow: "none",
          }}
        >
          {cartCount > 0 ? `Cart (${cartCount}) • $${cartTotal.toFixed(2)}` : "Cart"}
        </Button>
      </Box>

      {/* Body: product list + cart panel */}
      <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>

        {/* Product list */}
        <Box sx={{ flex: 1, overflow: "auto", p: { xs: 1.5, md: 2.5 } }}>
          <TextField
            size="small" fullWidth
            placeholder="Search products..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CiSearch style={{ fontSize: 16, color: "#999" }} />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2, bgcolor: "#fff", borderRadius: "10px", "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
          />

          {filtered.length === 0 && (
            <Box textAlign="center" py={6}>
              <Typography color="text.secondary">No products found</Typography>
            </Box>
          )}

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" }, gap: 1.5 }}>
            {filtered.map((p) => {
              const qty = cart[p.id] || 0;
              const price = getPrice(p);
              return (
                <Box
                  key={p.id}
                  sx={{
                    bgcolor: "#fff", borderRadius: "12px", p: 2,
                    border: qty > 0 ? "2px solid #3949ab" : "1px solid #e8eaf0",
                    transition: "all 0.15s",
                    boxShadow: qty > 0 ? "0 4px 16px rgba(57,73,171,0.12)" : "none",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 1.25 }}>
                    {p.productImg ? (
                      <Avatar
                        src={`${LARAVEL_URL}/${p.productImg}`}
                        variant="rounded"
                        sx={{ width: 48, height: 48, borderRadius: "8px", flexShrink: 0 }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: 48, height: 48, borderRadius: "8px", flexShrink: 0,
                          bgcolor: "#e8eaf6", display: "flex", alignItems: "center", justifyContent: "center",
                        }}
                      >
                        <StorefrontIcon sx={{ fontSize: 22, color: "#7986cb" }} />
                      </Box>
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography fontSize={13} fontWeight={600} noWrap>{p.productName}</Typography>
                      {p.productShortDescription && (
                        <Typography fontSize={11} color="text.secondary" noWrap>{p.productShortDescription}</Typography>
                      )}
                      <Typography fontSize={14} fontWeight={700} color={price > 0 ? "#2e7d32" : "text.disabled"} mt={0.25}>
                        {price > 0 ? `$${price.toFixed(2)}` : "—"}
                      </Typography>
                    </Box>
                  </Box>

                  {qty === 0 ? (
                    <Button
                      fullWidth variant="outlined"
                      onClick={() => adjust(p.id, 1)}
                      sx={{
                        borderRadius: "8px", fontWeight: 600, fontSize: 12,
                        borderColor: "#c5cae9", color: "#3949ab",
                        "&:hover": { borderColor: "#3949ab", bgcolor: "#e8eaf6" },
                      }}
                    >
                      Add to Order
                    </Button>
                  ) : (
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Button
                        variant="contained" size="small"
                        onClick={() => adjust(p.id, -1)}
                        sx={{
                          minWidth: 34, width: 34, height: 34, p: 0, borderRadius: "8px",
                          bgcolor: "#e8eaf6", color: "#3949ab", boxShadow: "none",
                          "&:hover": { bgcolor: "#c5cae9", boxShadow: "none" },
                        }}
                      >
                        <RemoveIcon sx={{ fontSize: 16 }} />
                      </Button>
                      <Box sx={{ textAlign: "center" }}>
                        <Typography fontWeight={700} fontSize={16}>{qty}</Typography>
                        <Typography fontSize={10} color="text.secondary">${(price * qty).toFixed(2)}</Typography>
                      </Box>
                      <Button
                        variant="contained" size="small"
                        onClick={() => adjust(p.id, 1)}
                        sx={{
                          minWidth: 34, width: 34, height: 34, p: 0, borderRadius: "8px",
                          bgcolor: "#3949ab", boxShadow: "none",
                          "&:hover": { bgcolor: "#1a237e", boxShadow: "none" },
                        }}
                      >
                        <AddIcon sx={{ fontSize: 16 }} />
                      </Button>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Cart sidebar (desktop: always visible if cartOpen or items in cart; mobile: slide-in) */}
        {(cartOpen || cartCount > 0) && (
          <Box
            sx={{
              width: { xs: "100%", sm: 300 },
              position: { xs: "absolute", sm: "relative" },
              right: 0, top: 0, bottom: 0,
              bgcolor: "#fff",
              borderLeft: { sm: "1px solid #e8eaf0" },
              display: "flex", flexDirection: "column",
              zIndex: 10,
              boxShadow: { xs: "-4px 0 20px rgba(0,0,0,0.12)", sm: "none" },
            }}
          >
            <Box sx={{ px: 2, py: 1.75, borderBottom: "1px solid #f0f0f5", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Typography fontWeight={700} fontSize={15}>Order Summary</Typography>
              <Button size="small" onClick={() => setCartOpen(false)} sx={{ display: { sm: "none" }, minWidth: 0, p: 0.5 }}>✕</Button>
            </Box>

            <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
              {cartItems.length === 0 ? (
                <Box textAlign="center" py={4}>
                  <ShoppingCartIcon sx={{ fontSize: 36, color: "#c5cae9", mb: 1 }} />
                  <Typography fontSize={13} color="text.secondary">No items yet</Typography>
                </Box>
              ) : (
                cartItems.map((p) => (
                  <Box key={p.id} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.25 }}>
                    <Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
                      <Typography fontSize={12} fontWeight={600} noWrap>{p.productName}</Typography>
                      <Typography fontSize={11} color="text.secondary">
                        {cart[p.id]} × ${getPrice(p).toFixed(2)}
                      </Typography>
                    </Box>
                    <Typography fontSize={13} fontWeight={700}>${(getPrice(p) * cart[p.id]).toFixed(2)}</Typography>
                    <Button
                      size="small"
                      onClick={() => setCart((prev) => { const n = {...prev}; delete n[p.id]; return n; })}
                      sx={{ minWidth: 0, ml: 0.5, p: 0.4, color: "#e53935", fontSize: 12 }}
                    >✕</Button>
                  </Box>
                ))
              )}
            </Box>

            <Box sx={{ p: 2, borderTop: "1px solid #f0f0f5" }}>
              <Divider sx={{ mb: 1.5 }} />
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1.5 }}>
                <Typography fontWeight={700} fontSize={14}>Total</Typography>
                <Typography fontWeight={800} fontSize={14} color="#2e7d32">${cartTotal.toFixed(2)}</Typography>
              </Box>
              <TextField
                size="small" fullWidth multiline rows={2}
                label="Order notes (optional)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                sx={{ mb: 1.5, "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
              />
              <Button
                fullWidth variant="contained"
                disabled={cartItems.length === 0 || submitting}
                onClick={handleSubmit}
                sx={{
                  borderRadius: "10px", py: 1.25, fontWeight: 700, fontSize: 14,
                  bgcolor: "#3949ab", "&:hover": { bgcolor: "#1a237e" },
                  "&:disabled": { bgcolor: "#c5cae9" },
                }}
              >
                {submitting ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : `Submit Order · $${cartTotal.toFixed(2)}`}
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Portal;
